<template>
  <div>
    <div class="page-head">
      <button type="button" class="entry" @click="$router.push('/student/attempts')">← All attempts</button>
      <h1 style="margin-top:12px">{{ subjectName }}</h1>
      <p>All attempts for this subject across windows.</p>
    </div>
    <dsa-loading v-if="loading" text="Loading attempts…" />
    <template v-else>
      <div v-if="!attempts.length" class="empty">
        <h3>No attempts for this subject</h3>
        <router-link to="/student/catalog" class="btn btn-outline">Browse catalog</router-link>
      </div>
      <table v-else>
        <thead>
          <tr>
            <th>Attempt</th>
            <th>Window</th>
            <th>Exam Date</th>
            <th>Status</th>
            <th>Score</th>
            <th style="text-align:right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in attempts" :key="row.application_id">
            <td>{{ row.attempt_num }}</td>
            <td>{{ row.window_label || row.quarter_code }}</td>
            <td>{{ row.exam_date || '—' }}</td>
            <td>
              <span v-if="isPending(row)" class="chip pend">Pending</span>
              <span v-else-if="!row.score" class="chip soon">Scheduled</span>
              <span v-else class="chip open">Completed</span>
            </td>
            <td>
              <span v-if="!row.score">—</span>
              <span v-else-if="scoreView(row.score)" class="score-cell">{{ scoreView(row.score).text }}</span>
              <span v-else class="chip pend">Results pending</span>
            </td>
            <td class="td-actions" style="text-align:right">
              <template v-if="isPending(row)">
                <button
                  type="button"
                  class="entry"
                  :disabled="redeemingId === row.application_id"
                  @click="redeem(row)"
                >{{ redeemingId === row.application_id ? 'Redeeming…' : 'Redeem' }}</button>
                <button type="button" class="entry ext" @click="cancel(row)">Cancel</button>
              </template>
              <template v-else-if="scoreReleased(row.score)">
                <button type="button" class="entry" @click="openReport(row.application_id)">Score report</button>
              </template>
              <template v-else>
                <button type="button" class="entry" @click="viewExam(row)">View exam</button>
                <button type="button" class="entry ext" @click="cancel(row)">Cancel</button>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <exam-schedule-modal
      :open="examModalOpen"
      :loading="examModalLoading"
      :subject-name="examModalSubject"
      :project-name="examModalProject"
      :sessions="examModalSessions"
      :exam-system-url="examSystemUrl"
      @close="examModalOpen = false"
    />
    <redeem-pass-modal
      :open="redeemModalOpen"
      :step="redeemStep"
      :subject-name="redeemSubjectName"
      :busy="!!redeemingId"
      @close="closeRedeemModal"
      @confirm="confirmRedeem"
      @purchase="goPurchasePass"
    />
  </div>
</template>

<script>
import { getStudentAttemptsGrouped, cancelRegistration, redeemDsaApplicationWithPass } from '@/api/dsa'
import { getUserId } from '@/utils/store'
import { formatScoreDisplay, isPendingPayment } from '@/utils/dsaFormat'
import { loadExamSchedule } from '@/utils/examEntry'
import ExamScheduleModal from '@/components/ExamScheduleModal.vue'
import RedeemPassModal from '@/components/RedeemPassModal.vue'

export default {
  name: 'StudentAttemptsSubject',
  components: { ExamScheduleModal, RedeemPassModal },
  data() {
    return {
      loading: true,
      subjectName: '',
      attempts: [],
      redeemingId: '',
      redeemModalOpen: false,
      redeemStep: 'confirm',
      redeemSubjectName: '',
      redeemTarget: null,
      examModalOpen: false,
      examModalLoading: false,
      examModalSubject: '',
      examModalProject: '',
      examModalSessions: [],
      examSystemUrl: 'https://exam.learningfirst.io/',
    }
  },
  watch: {
    '$route.params.subjectCode'() {
      this.load()
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    scoreView(score) {
      return formatScoreDisplay(score)
    },
    scoreReleased(score) {
      const sv = formatScoreDisplay(score)
      return !!(sv && score && score.visible !== false && !score.pending)
    },
    isPending(row) {
      return isPendingPayment(row)
    },
    openReport(id) {
      this.$router.push(`/student/transcript/${id}`)
    },
    printReport(id) {
      this.openReport(id)
    },
    async viewExam(row) {
      this.examModalOpen = true
      this.examModalLoading = true
      this.examModalSubject = this.subjectName || ''
      this.examModalProject = ''
      this.examModalSessions = []
      const data = await loadExamSchedule(this, row.application_id)
      this.examModalLoading = false
      if (!data) {
        this.examModalOpen = false
        return
      }
      this.examModalSubject = data.subject_name || this.examModalSubject
      this.examModalProject = data.project_name || ''
      this.examModalSessions = data.exam_sessions || []
      if (data.exam_system_url) this.examSystemUrl = data.exam_system_url
    },
    async redeem(row) {
      this.redeemTarget = row
      this.redeemSubjectName = this.subjectName || (row && row.subject_name) || 'this subject'
      this.redeemStep = 'confirm'
      this.redeemModalOpen = true
    },
    closeRedeemModal() {
      if (this.redeemingId) return
      this.redeemModalOpen = false
      this.redeemTarget = null
      this.redeemStep = 'confirm'
    },
    goPurchasePass() {
      this.redeemModalOpen = false
      this.redeemTarget = null
      this.redeemStep = 'confirm'
      this.$router.push('/student/pass')
    },
    async confirmRedeem() {
      const row = this.redeemTarget
      if (!row || !row.application_id) return
      this.redeemingId = row.application_id
      try {
        const res = await redeemDsaApplicationWithPass(getUserId(), row.application_id)
        if (res.data && res.data.code === 0) {
          this.$emit('toast', 'Pass redeemed')
          this.redeemModalOpen = false
          this.redeemTarget = null
          this.redeemStep = 'confirm'
          this.load()
        } else if (res.data && res.data.code === -5) {
          this.redeemStep = 'no_pass'
        } else {
          this.$emit('toast', (res.data && res.data.msg) || 'Redeem failed')
        }
      } catch (e) {
        this.$emit('toast', 'Redeem failed')
      } finally {
        this.redeemingId = ''
      }
    },
    async cancel(row) {
      if (!window.confirm(`Cancel this registration? Your Pass attempt will be restored.`)) return
      try {
        const res = await cancelRegistration(row.application_id)
        if (res.data && res.data.code === 0) {
          this.$emit('toast', 'Registration cancelled')
          this.load()
        } else {
          this.$emit('toast', (res.data && res.data.msg) || 'Cancel failed')
        }
      } catch (e) {
        this.$emit('toast', 'Cancel failed')
      }
    },
    async load() {
      this.loading = true
      const code = (this.$route.params.subjectCode || '').toUpperCase()
      try {
        const res = await getStudentAttemptsGrouped(getUserId(), code)
        const groups = (res.data && res.data.data) || []
        const group = groups.find(g => (g.subject_code || '').toUpperCase() === code) || groups[0]
        if (group) {
          this.subjectName = group.subject_name || group.subject_code || code
          this.attempts = group.attempts || []
        } else {
          this.subjectName = code
          this.attempts = []
        }
      } catch (e) {
        this.$emit('toast', 'Failed to load attempts')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
